/**
 * Your Plan's figures, rendered from the report model so they agree with the report: the estimated
 * cost, self-funding, the total projected coverage and the gap ("Fully covered" at 0), then a card per
 * policy: its carrier logo (with a bin to remove the policy added from a quote), its name and status,
 * and its projected payout. Each state of the calculation renders on its own terms; a missing number
 * never appears as $0.
 */
import type { ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@design-system';
import type { FundingCalculation } from '@/features/funding-sources';
import {
  YOUR_PLAN_LABELS,
  formatPlanGap,
  formatPlanMoney,
  formatPolicyCoverage,
  isFullyCovered,
  type YourPlanReportModel,
} from '@/features/pdf-report';
import { getCarrierLogoSrc } from '@/util/carrierLogo';
import { DetailList, type DetailRow } from './figures';
import { PolicyStatusBadge } from './policy-status-badge';
import { RemoveAddedQuoteButton } from './remove-added-quote-button';

function PolicyLines({ clientId, plan }: { clientId: string; plan: YourPlanReportModel }) {
  return (
    <ul
      className="divide-y rounded-lg border bg-white"
      data-testid="your-plan-policies"
    >
      {plan.policies.map(policy => (
        <li
          key={policy.key}
          className="flex flex-col gap-3 p-4 text-sm"
          data-testid="your-plan-policy"
        >
          {(policy.carrierName || policy.isAddedQuote) && (
            <div className="flex items-center gap-4">
              {policy.carrierName && (
                <img
                  src={getCarrierLogoSrc(policy.carrierName)}
                  alt=""
                  className="h-6 max-w-24 object-contain object-left"
                  // A recorded policy can name a carrier we have no logo for.
                  onError={event => {
                    event.currentTarget.hidden = true;
                  }}
                />
              )}
              {policy.isAddedQuote && (
                <span className="ml-auto">
                  <RemoveAddedQuoteButton
                    clientId={clientId}
                    hasApplication={policy.status === 'application-submitted'}
                    appearance="icon"
                  />
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-950">{policy.name}</span>
            {policy.status && <PolicyStatusBadge status={policy.status} />}
          </div>
          <div className="flex items-baseline justify-between gap-4 border-t pt-3">
            <span className="text-muted-foreground">Projected payout for your care scenario</span>
            <span className="text-right text-base font-semibold tabular-nums text-gray-950">
              {formatPolicyCoverage(policy.projectedCoverage)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function YourPlanSummaryList({
  clientId,
  summary,
  afterRows,
}: {
  clientId: string;
  summary: FundingCalculation<YourPlanReportModel>;
  /** Shown right after the figures, such as the renewal offer that replaces self-funding. */
  afterRows?: ReactNode;
}) {
  switch (summary.status) {
    case 'loading':
      return (
        <div
          className="flex flex-col gap-2"
          data-testid="your-plan-summary-loading"
        >
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      );
    case 'failed':
      return (
        <Alert variant="destructive">
          <AlertTitle>We couldn’t load your plan’s figures</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            Please try again in a moment.
            <Button
              variant="outline"
              size="sm"
              onClick={summary.retry}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      );
    case 'unavailable':
      return (
        <Alert>
          <AlertTitle>Your plan’s figures aren’t ready yet</AlertTitle>
          <AlertDescription>
            We’re still working out some of your numbers. Please check back in a moment.
          </AlertDescription>
        </Alert>
      );
    case 'ready': {
      const plan = summary.value;
      const rows: DetailRow[] = [
        { label: YOUR_PLAN_LABELS.estimatedCost, value: formatPlanMoney(plan.estimatedCost) },
        ...(plan.selfFunding !== null
          ? [{ label: YOUR_PLAN_LABELS.selfFunding, value: formatPlanMoney(plan.selfFunding) }]
          : []),
        { label: YOUR_PLAN_LABELS.combined, value: formatPlanMoney(plan.combined) },
        {
          label: YOUR_PLAN_LABELS.gap,
          value: formatPlanGap(plan.gap),
          tone: isFullyCovered(plan.gap) ? 'positive' : 'default',
        },
      ];
      return (
        <div
          className="flex flex-col gap-4"
          data-testid="your-plan-summary"
        >
          <DetailList rows={rows} />
          {afterRows}
          {plan.policies.length > 0 && (
            <PolicyLines
              clientId={clientId}
              plan={plan}
            />
          )}
        </div>
      );
    }
  }
}
